// src/ui.ts —— 原型 L298–L304（cell / chip / act / btn / field / li）与 renderVals 内 statCard、th、页签、页码样式逐字移植（各域只读复用）
import type { ClickEvent } from './domains/types';

export type ChipKind = 'green' | 'gray' | 'amber' | 'red' | 'blue' | 'purple';

const CHIP: Readonly<Record<ChipKind, readonly [string, string]>> = {
  green: ['#e3f6ee', '#0d9a68'],
  gray: ['#eef3f0', '#6b7772'],
  amber: ['#fff3df', '#c47a0c'],
  red: ['#fdecec', '#d1434b'],
  blue: ['#e7f3fb', '#1f6fa8'],
  purple: ['#ece9fb', '#6b4fd8'],
};

export interface CellOut {
  v: string;
  sub: string;
  isPlain: boolean;
  isStatus: boolean;
  chip: string;
  style: string;
}

export function chipStyle(kind: ChipKind): string {
  const [bg, fg] = CHIP[kind];
  return `display:inline-block;padding:3px 9px;border-radius:999px;font-size:11px;font-weight:700;background:${bg};color:${fg};white-space:nowrap`;
}

export function cell(v: string | number, sub = '', mono = false): CellOut {
  return {
    v: String(v),
    sub,
    isPlain: true,
    isStatus: false,
    chip: '',
    style: `padding:12px 16px;vertical-align:middle;${mono ? "font-family:'JetBrains Mono',monospace;font-size:12px;" : ''}white-space:nowrap`,
  };
}

export function chip(kind: ChipKind, label: string): CellOut {
  return { v: label, sub: '', isStatus: true, isPlain: false, chip: chipStyle(kind), style: 'padding:12px 16px;vertical-align:middle' };
}

export interface ActOut {
  label: string;
  run: (e: ClickEvent) => void;
  style: string;
}

export function act(label: string, run: () => void, danger = false): ActOut {
  return {
    label,
    run: (e) => {
      e.stopPropagation();
      run();
    },
    style: `height:28px;padding:0 10px;margin-left:6px;border:1px solid ${danger ? '#f3c2c5' : '#e6ebe8'};border-radius:7px;background:#fff;color:${danger ? '#d1434b' : '#0f1a17'};font-size:12px;font-weight:700;cursor:pointer`,
  };
}

export interface BtnOut {
  label: string;
  run: () => void;
  style: string;
}

export function btn(label: string, run: () => void, kind?: 'primary' | 'green'): BtnOut {
  const bg = kind === 'primary' ? '#0f1a17' : kind === 'green' ? '#0d9a68' : '#fff';
  return {
    label,
    run,
    style: `height:40px;padding:0 16px;border:1px solid ${kind ? 'transparent' : '#e6ebe8'};border-radius:10px;background:${bg};color:${kind ? '#fff' : '#0f1a17'};font-weight:700;font-size:13px;cursor:pointer`,
  };
}

export interface FieldOut {
  k: string;
  v: string;
  style: string;
}

export function field(k: string, v: string | number, wide = false): FieldOut {
  return { k, v: String(v), style: `background:#f8faf9;border-radius:10px;padding:10px 12px;${wide ? 'grid-column:1/-1' : ''}` };
}

export interface LiOut {
  left: string;
  sub: string;
  right: string;
  rightSub: string;
}

export function li(left: string, sub = '', right = '', rightSub = ''): LiOut {
  return { left, sub, right, rightSub };
}

export interface StatOut {
  label: string;
  value: string;
  sub: string;
  style: string;
}

export function statCard(label: string, value: string, sub: string, dark = false): StatOut {
  return {
    label,
    value,
    sub,
    style: `border-radius:16px;padding:16px 18px;border:1px solid ${dark ? 'transparent' : '#e6ebe8'};background:${dark ? '#0f1a17' : '#fff'};color:${dark ? '#fff' : '#0f1a17'}`,
  };
}

// 原型 L320–L322 导航项样式
export function navStyle(active: boolean): string {
  return `display:flex;align-items:center;gap:8px;height:44px;padding:0 14px;border:0;background:transparent;cursor:pointer;font-size:13px;font-weight:700;white-space:nowrap;color:${active ? '#39c88f' : 'rgba(255,255,255,.65)'};border-bottom:3px solid ${active ? '#39c88f' : 'transparent'}`;
}

// 原型 L356 页签样式
export function tabStyle(active: boolean): string {
  return `height:34px;padding:0 14px;border:1px solid ${active ? '#0f1a17' : '#e6ebe8'};border-radius:999px;background:${active ? '#0f1a17' : '#fff'};color:${active ? '#fff' : '#6b7772'};font-size:13px;font-weight:700;cursor:pointer`;
}

// 原型 L311–L314 表头样式
export function thStyle(active: boolean, sortable: boolean): string {
  return `padding:11px 16px;text-align:left;font-size:11px;color:${active ? '#0d9a68' : '#6b7772'};font-weight:700;letter-spacing:.3px;text-transform:uppercase;white-space:nowrap;${sortable ? 'cursor:pointer;user-select:none' : ''}`;
}

// 原型 L377 页码样式
export function pageBtnStyle(current: boolean): string {
  return `height:28px;min-width:30px;padding:0 8px;border:${current ? '0' : '1px solid #e6ebe8'};border-radius:7px;background:${current ? '#0f1a17' : '#fff'};color:${current ? '#fff' : '#0f1a17'};font-size:12px;font-weight:${current ? 800 : 600};cursor:pointer`;
}

export function pagerStyle(enabled: boolean): string {
  return `height:28px;padding:0 10px;border:1px solid #e6ebe8;border-radius:7px;background:#fff;font-size:12px;cursor:${enabled ? 'pointer' : 'not-allowed'};opacity:${enabled ? 1 : 0.5}`;
}

// 费率串去尾零：'10.000000' → '10'，'0.600000' → '0.6'（百分比 / 原样展示用；金额一律 fmtAmount）
export function trimNum(s: string): string {
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}
